import { useEffect, useState } from "react";
import { Flex } from "@chakra-ui/layout";
import { Input, Row, Col, Table } from "antd";
import { MdFastForward, MdSchedule, MdDescription } from "react-icons/md";
import { useFilters } from "core/features/filters/store";
import useStore from "../../hooks/useStore";
import getColumns from "./columns";
import {
  StyledModal,
  Title,
  ContentButton,
  ButtonStyled,
  ButtonStyledLink,
  StyledSelect,
  AcoesHeader,
  AcoesTable,
} from "../../styles";

const { TextArea } = Input;

interface ModalAcoesProps {
  data: any[];
  isLoading: boolean;
  planoOperacionalData: any;
  action: "antecipar" | "prorrogar" | "justificar";
  isOpen: boolean;
  onClose: () => void;
  next: () => void;
}

function ModalAcoes({
  data,
  isLoading,
  action,
  isOpen,
  planoOperacionalData,
  next,
  onClose,
}: ModalAcoesProps) {

  const {
    planoOperacionalOperacaoTalhaoIds,
    motivo,
    observacao,
    reagendamentoQuantidadeDias,
    reset,
    setMotivo,
    setObservacao,
    setReagendamentoQuantidadeDias,
  } = useStore();

  const talhoes = data?.flatMap(item => item?.planoOperacionalOperacaoTalhao?.map((t: any) => ({ ...t, codigo: item.codigo })));
  const talhoesSequencias = talhoes?.filter((item: any) => planoOperacionalOperacaoTalhaoIds.includes(item?.planoOperacionalOperacaoTalhaoId)) ?? [];

  const [isMotivoValid, setIsMotivoValid] = useState<boolean>();
  const [isObservacaoValid, setIsObservacaoValid] = useState<boolean>();
  const [isReagendamentoQuantidadeDiasValid, setIsReagendamentoQuantidadeDiasValid] = useState<boolean>();

  const isSubmitAllowed = (action === "justificar" ? true : !!reagendamentoQuantidadeDias) && motivo !== undefined && !!observacao;

  const { filters } = useFilters();
  const { filtersAdvanced } = filters;
  const { classeOperacao } = filtersAdvanced;

  const filtrosAvancados = planoOperacionalData?.filtrosAvancados;
  const operacaoClasses = filtrosAvancados?.operacaoClasses;
  const classesOperacoes = classeOperacao?.map(item => {
    const classe = operacaoClasses?.find((i: any) => i.operacaoClasseId === item);
    return classe?.descricao;
  });

  const motivos = [
    { value: 0, label: "Climático" },
    { value: 1, label: "Decisão Agronômica" },
    { value: 2, label: "Problema Operacional" },
    { value: 3, label: "Capacidade Operacional" },
  ];

  function handleSubmit() {
    setIsObservacaoValid(!!observacao);
    setIsMotivoValid(motivo !== undefined);
    setIsReagendamentoQuantidadeDiasValid(action === "justificar" ? true : !!reagendamentoQuantidadeDias);
    if (!isSubmitAllowed) return;
    next();
  }

  function getTitle() {
    if (action === "antecipar") {
      return (
        <>
          <MdFastForward color="#fff" size={28} />
          Antecipar Operação
        </>
      );
    }
    if (action === "prorrogar") {
      return (
        <>
          <MdSchedule color="#fff" size={28} />
          Prorrogar Operação
        </>
      );
    }
    return (
      <>
        <MdDescription color="#fff" size={28} />
        Justificar Atraso
      </>
    );
  }

  function getColor() {
    if (action === "antecipar") {
      return "#27ae60";
    }
    if (action === "prorrogar") {
      return "#f39c12";
    }
    return "#12619C";
  }

  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planoOperacionalData]);

  return (
    <StyledModal
      centered
      color={getColor()}
      width={650}
      zIndex={999999}
      visible={isOpen}
      onCancel={onClose}
      title={<Title>{getTitle()}</Title>}
      footer={
        <>
          <Flex justifyContent="end" alignItems="center">
            <ContentButton>
              <ButtonStyledLink
                size="large"
                onClick={onClose}
                loading={isLoading}
              >Cancelar</ButtonStyledLink>
              <ButtonStyled
                color={getColor()}
                type="primary"
                size="large"
                onClick={handleSubmit}
                loading={isLoading}
              >Confirmar</ButtonStyled>
            </ContentButton>
          </Flex>
        </>
      }
    >
      {talhoesSequencias.length > 0 && (
        <AcoesHeader>
          <p>Talhões e Sequências Selecionadas:</p>
          <div>
            {talhoesSequencias?.map((talhao: any) => (
              <p>{talhao?.codigo} - {talhao?.sequencia}ª {talhao?.operacaoClasse}</p>
            ))}
          </div>
        </AcoesHeader>
      )}

      <Row gutter={[16, 16]} style={{ padding: 16 }}>
        <Col span={24}>
          <p style={{ marginBottom: 4, fontWeight: "bold" }}>Operações</p>
          <StyledSelect
            disabled
            allowClear
            size="large"
            mode="multiple"
            maxTagCount={2}
            maxTagPlaceholder={(omittedValues) => <span>+{omittedValues.length}</span>}
            value={classesOperacoes}
            style={{ borderRadius: 10 }}
          />
        </Col>
        {action !== "justificar" && (
          <Col span={24}>
            <p style={{ marginBottom: 4, fontWeight: "bold" }}>Quantidade de Dias <sup>*</sup></p>
            <Flex align="center" gridGap={2}>
              <Input
                allowClear
                type="number"
                size="large"
                placeholder="Ex: 7"
                value={reagendamentoQuantidadeDias}
                onChange={e => setReagendamentoQuantidadeDias(Number(e.target.value))}
                style={{
                  borderRadius: 10,
                  width: "60%",
                  borderColor: isReagendamentoQuantidadeDiasValid === false ? "#f00" : "#d9d9d9",
                }}
              />
              <p style={{ fontWeight: "bold", color: getColor() }}>
                dias para {action?.charAt(0).toUpperCase() + action?.slice(1)}
              </p>
            </Flex>
          </Col>
        )}
        <Col span={24}>
          <p style={{ marginBottom: 4, fontWeight: "bold" }}>Motivo <sup>*</sup></p>
          <StyledSelect
            allowClear
            showSearch
            optionFilterProp="text"
            size="large"
            placeholder="Motivo"
            options={motivos}
            value={motivo}
            onChange={e => setMotivo(e as number)}
            borderColor={isMotivoValid === false ? "#f00" : "#d9d9d9"}
          />
        </Col>
        <Col span={24}>
          <p style={{ marginBottom: 4, fontWeight: "bold" }}>Observação <sup>*</sup></p>
          <TextArea
            size="large"
            placeholder="Descreva os detalhes da justificativa..."
            rows={3}
            value={observacao}
            onChange={e => setObservacao(e.target.value)}
            style={{
              borderRadius: 10,
              borderColor: isObservacaoValid === false ? "#f00" : "#d9d9d9",
            }}
          />
        </Col>
      </Row>

      {reagendamentoQuantidadeDias && action !== "justificar" && (
        <AcoesTable>
          <p>Preview das Novas Datas:</p>
          <Table
            dataSource={talhoesSequencias}
            columns={getColumns(action, reagendamentoQuantidadeDias)}
            pagination={false}
          />
        </AcoesTable>
      )}
    </StyledModal>
  );
}

export default ModalAcoes;
